import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import {
  addToFavorites,
  fetchMentors,
  getAssociatedMentor,
  getFavoriteMentors,
  getFavoriteMentorsIds,
  getFeaturedMentors,
  getHomepageData,
  getMainCarouselData,
  getResentUserContexts,
  removeFromFavorites,
} from '../../api/personas'
import { IPaginatedResponse } from '../../types/contextGroup'
import { MentorBase } from '../../types/mentor'

export const useAllMentors = (filters: Record<string, string | null>) =>
  useInfiniteQuery({
    queryKey: ['get-all-mentors', filters],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchMentors(pageParam, filters),
    getNextPageParam: data =>
      data.page_index === data.total_page ? undefined : data.page_index + 1,
  })

export interface IFavoriteMentorsParams {
  currentPage: number
}

export interface IFavoriteMentorsResult {
  items: MentorBase[]
  page_index: number
  total_page: number
  totalItems: number
}

export const useFavoriteMentors = (page: number) =>
  useQuery<IFavoriteMentorsParams, AxiosError, IFavoriteMentorsResult>({
    queryKey: ['get-favorite-mentors', page],
    queryFn: () => getFavoriteMentors(page),
  })

export const useFavoriteMentorsIds = () =>
  useQuery({
    queryKey: ['get-favorite-mentors-ids'],
    queryFn: getFavoriteMentorsIds,
    enabled: false,
  })
export const useAddToFavorites = () =>
  useMutation({ mutationFn: addToFavorites })

export const useRemoveFromFavorites = () =>
  useMutation({ mutationFn: removeFromFavorites })

export const useHomepageData = (enabled: boolean) =>
  useQuery({
    queryKey: ['get-home-mentors'],
    queryFn: getHomepageData,
    enabled,
  })

export const useFeaturedMentors = (enabled: boolean) =>
  useQuery({
    queryKey: ['get-featured-mentors'],
    queryFn: getFeaturedMentors,
    enabled,
  })

export const useRecentContext = () =>
  useInfiniteQuery<IPaginatedResponse>({
    queryKey: ['get-recent-contexts'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => getResentUserContexts(pageParam as number),
    getNextPageParam: resp =>
      resp.page_index + 1 === resp.total_page ? undefined : resp.page_index + 1,
  })

export const useAssociatedMentor = (contextId?: string) =>
  useQuery({
    queryKey: ['get-associated-mentor', contextId],
    queryFn: () => getAssociatedMentor(contextId || ''),
    enabled: !!contextId,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchInterval: ({ state }) => {
      const status = state?.data?.status
      if (
        status === 'Failed' ||
        status === 'Completed' ||
        status === 'ReadyToUse' ||
        state.error
      )
        return false
      return 3000
    },
  })

export const useMainCarouselData = () =>
  useQuery({
    queryKey: ['get-main-carousel-data'],
    queryFn: getMainCarouselData,
  })
